/*
  Dent & Deals fulfillment scheduler.
  Loaded on every page from layout/theme.liquid so the cart drawer's
  "Apply options & update total" button works even when the page was
  opened with an empty cart (Shopify only includes {% javascript %}
  snippet code when the snippet rendered during the initial page load).
*/
(() => {
  if (window.__ddSchedulerLoaded) return;
  // If the snippet's compiled {% javascript %} copy already ran, don't bind twice.
  const compiled = document.getElementById('snippets-script');
  if (compiled && (compiled.getAttribute('data-snippets') || '').split(',').includes('dent-deals-fulfillment-scheduler')) return;
  window.__ddSchedulerLoaded = true;

  const controllers = new WeakMap();
  let queue = Promise.resolve();
  let checkoutBusy = false;
  const appliedFlag = 'dd-options-applied-v2';
  const SERVICE_VARIANTS = {
    installation: 45045704786013,
    haulAway: 45045704753245,
    saturday: 45045704818781,
    rush: 45045704851549,
    delivery10: 45046760767581,
    delivery25: 45046760800349
  };
  const serviceIds = new Set(Object.values(SERVICE_VARIANTS));
  const DELIVERY_ONLY_KEYS = ['Delivery ZIP code', 'Delivery zone', 'Delivery speed', 'Saturday priority delivery', 'Installation requested', 'Haul-away requested', 'Delivery access confirmed'];
  const shopRoot = () => window.Shopify?.routes?.root || '/';
  const money = (cents, currency) => new Intl.NumberFormat('en-US', {style:'currency', currency:currency || 'USD'}).format(cents / 100);
  const checkoutFor = root => root.parentElement?.parentElement?.querySelector('#checkout');
  const controllerFor = root => {
    if (!controllers.has(root)) controllers.set(root, createController(root));
    return controllers.get(root);
  };

  const createController = root => {
  const ZIP_ZONES = {
    free: new Set(['46201','46216','46218','46219','46226','46229','46235','46239']),
    ten: new Set(['46055','46107','46163','46202','46203','46204','46205','46208','46220','46225','46228','46236','46237','46240','46250','46256','46259']),
    twentyFive: new Set(['46013','46032','46033','46034','46037','46038','46040','46048','46051','46056','46060','46062','46064','46074','46075','46077','46104','46106','46110','46112','46113','46115','46117','46123','46126','46130','46131','46140','46142','46143','46144','46154','46158','46161','46162','46167','46168','46176','46184','46186','46214','46217','46221','46222','46224','46227','46231','46234','46241','46254','46260','46268','46278','46280','46290'])
  };
  // Always look elements up fresh: the cart section is re-rendered (morphed) after
  // quantity changes, and cached references can point at detached nodes.
  const els = () => ({
    speed: root.querySelector('[data-dd-speed]'),
    zip: root.querySelector('[data-dd-zip]'),
    zone: root.querySelector('[data-dd-zone]'),
    zoneMessage: root.querySelector('[data-dd-zone-message]'),
    date: root.querySelector('[data-dd-date]'),
    access: root.querySelector('[data-dd-access]'),
    notice: root.querySelector('[data-dd-notice]'),
    saturday: root.querySelector('[data-dd-saturday]'),
    install: root.querySelector('[data-dd-install]'),
    haul: root.querySelector('[data-dd-haul]')
  });
  const preference = () => root.querySelector('input[name="attributes[Fulfillment preference]"]:checked')?.value;
  const inCartDrawer = Boolean(root.closest('.cart-drawer__summary'));
  const cartRoot = shopRoot();
  const setNotice = text => { const n = els().notice; if (n) n.textContent = text; };

  const parts = () => Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Indiana/Indianapolis', year: 'numeric', month: '2-digit',
    day: '2-digit', hour: '2-digit', hour12: false
  }).formatToParts(new Date()).map(x => [x.type, x.value]));

  const easternDate = () => {
    const p = parts();
    return new Date(Number(p.year), Number(p.month) - 1, Number(p.day));
  };
  const iso = d => [d.getFullYear(), String(d.getMonth()+1).padStart(2,'0'), String(d.getDate()).padStart(2,'0')].join('-');
  const nextBusiness = () => {
    const d = easternDate();
    do d.setDate(d.getDate()+1); while (d.getDay() === 0);
    return d;
  };

  const classifyZip = () => {
    const {zip, zone, zoneMessage} = els();
    const value = zip.value.replace(/\D/g, '').slice(0, 5);
    if (zip.value !== value) zip.value = value;
    if (value.length !== 5) {
      zone.value = '';
      zoneMessage.textContent = 'Enter a five-digit ZIP code to see your delivery zone.';
      return 'invalid';
    }
    if (ZIP_ZONES.free.has(value)) {
      zone.value = 'Free local delivery zone';
      zoneMessage.textContent = 'Free local delivery is available for this ZIP code.';
      return 'free';
    }
    if (ZIP_ZONES.ten.has(value)) {
      zone.value = 'Extended local delivery zone — $50';
      zoneMessage.textContent = 'This ZIP code is in our extended local delivery zone. A $50 delivery charge will be added to your cart.';
      return 'ten';
    }
    if (ZIP_ZONES.twentyFive.has(value)) {
      zone.value = 'Regional delivery zone — $100';
      zoneMessage.textContent = 'This ZIP code is in our regional delivery zone. A $100 delivery charge will be added to your cart.';
      return 'twentyFive';
    }
    zone.value = 'Outside standard delivery area';
    zoneMessage.textContent = 'This ZIP code is outside our standard 25-mile delivery area. Please contact us before ordering delivery.';
    return 'outside';
  };

  const update = () => {
    const {speed, zip, date, access, saturday} = els();
    const delivery = preference() === 'Local delivery';
    root.querySelectorAll('.dd-delivery-only').forEach(el => el.hidden = !delivery);
    access.required = delivery;
    speed.required = delivery;
    zip.required = delivery;
    if (delivery) {
      classifyZip();
    } else {
      zip.setCustomValidity('');
    }
    const p = parts();
    const today = easternDate();
    const rushAllowed = Number(p.hour) < 12 && today.getDay() !== 0;
    const rushOption = speed.querySelector('option[value="Same-day rush — $50"]');
    rushOption.disabled = !rushAllowed;

    if (!delivery) {
      date.min = iso(today);
      date.removeAttribute('max');
    } else if (speed.value === 'Same-day rush — $50' && rushAllowed) {
      date.min = iso(today);
      date.max = iso(today);
      date.value = iso(today);
    } else {
      if (speed.value === 'Same-day rush — $50') speed.value = 'Standard — next business day or later';
      date.min = iso(nextBusiness());
      date.removeAttribute('max');
      if (date.value && date.value < date.min) date.value = '';
    }

    const selectedDay = date.value ? new Date(date.value + 'T12:00:00').getDay() : null;
    saturday.value = delivery && selectedDay === 6 ? 'Yes — $50' : '';
    return {delivery, selectedDay};
  };

  const defaultNotice = () => {
    const {delivery, selectedDay} = update();
    setNotice(delivery
      ? (selectedDay === 6
          ? 'Saturday Priority Delivery adds $50. Your requested window will be confirmed after order review.'
          : 'Delivery dates and time windows are requests and will be confirmed after order review. Sunday delivery is unavailable.')
      : 'Pickup is available after you receive confirmation that your order is ready.');
  };

  const validate = () => {
    update();
    const {zip, date} = els();
    if (preference() === 'Local delivery') {
      const zipClass = classifyZip();
      if (zipClass === 'invalid') {
        zip.setCustomValidity('Enter a valid five-digit delivery ZIP code.');
      } else if (zipClass === 'outside') {
        zip.setCustomValidity('This ZIP code is outside our standard delivery area. Please contact us before placing a delivery order.');
      } else {
        zip.setCustomValidity('');
      }
    } else {
      zip.setCustomValidity('');
    }
    if (date.value && new Date(date.value + 'T12:00:00').getDay() === 0) {
      date.setCustomValidity('Sunday appointments are unavailable. Please choose another date.');
    } else {
      date.setCustomValidity('');
    }
    const required = [...root.querySelectorAll('[required]')].filter(el => !el.closest('[hidden]'));
    const invalid = required.find(el => !el.reportValidity());
    if (invalid) {
      root.scrollIntoView({behavior:'smooth', block:'center'});
      return false;
    }
    return true;
  };

  const attributes = () => {
    const values = {};
    root.querySelectorAll('[name^="attributes["]').forEach(el => {
      const match = el.name.match(/^attributes\[(.*)\]$/);
      if (!match) return;
      if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) {
        if (!(match[1] in values)) values[match[1]] = '';
      } else {
        values[match[1]] = el.value;
      }
    });
    // Pickup orders should not carry leftover delivery details onto the order.
    if (values['Fulfillment preference'] !== 'Local delivery') {
      DELIVERY_ONLY_KEYS.forEach(key => { if (key in values) values[key] = ''; });
    }
    return values;
  };

  const desiredServices = () => {
    const {speed, date, install, haul} = els();
    const delivery = preference() === 'Local delivery';
    const wanted = new Set();
    const zipClass = delivery ? classifyZip() : null;
    if (zipClass === 'ten') wanted.add(SERVICE_VARIANTS.delivery10);
    if (zipClass === 'twentyFive') wanted.add(SERVICE_VARIANTS.delivery25);
    if (delivery && install.checked) wanted.add(SERVICE_VARIANTS.installation);
    if (delivery && haul.checked) wanted.add(SERVICE_VARIANTS.haulAway);
    if (delivery && speed.value === 'Same-day rush — $50') wanted.add(SERVICE_VARIANTS.rush);
    if (delivery && date.value && new Date(date.value + 'T12:00:00').getDay() === 6) wanted.add(SERVICE_VARIANTS.saturday);
    return wanted;
  };

  const syncCart = async () => {
    update();
    const cartResponse = await fetch(cartRoot + 'cart.js', {cache:'no-store', headers:{Accept:'application/json'}});
    if (!cartResponse.ok) throw new Error('Unable to read cart.');
    const cart = await cartResponse.json();
    const wanted = desiredServices();
    const selectedAttributes = attributes();
    const updates = {};
    const present = new Set();

    cart.items.forEach(item => {
      if (!serviceIds.has(item.variant_id)) return;
      // Drop duplicate lines of the same service; keep exactly one of each wanted service.
      if (present.has(item.variant_id)) { updates[item.key] = 0; return; }
      present.add(item.variant_id);
      if (!wanted.has(item.variant_id) || item.quantity !== 1) updates[item.key] = wanted.has(item.variant_id) ? 1 : 0;
    });

    const updateResponse = await fetch(cartRoot + 'cart/update.js', {
      method:'POST',
      headers:{'Content-Type':'application/json', Accept:'application/json'},
      body:JSON.stringify({attributes:selectedAttributes, updates})
    });
    if (!updateResponse.ok) throw new Error('Unable to save your delivery selections.');

    const additions = [...wanted].filter(id => !present.has(id)).map(id => ({id, quantity:1}));
    if (additions.length) {
      const addResponse = await fetch(cartRoot + 'cart/add.js', {
        method:'POST',
        headers:{'Content-Type':'application/json', Accept:'application/json'},
        body:JSON.stringify({items:additions})
      });
      if (!addResponse.ok) throw new Error('Unable to add your selected service charges.');
    }

    const verifiedResponse = await fetch(cartRoot + 'cart.js', {cache:'no-store', headers:{Accept:'application/json'}});
    if (!verifiedResponse.ok) throw new Error('Unable to verify your cart.');
    const verified = await verifiedResponse.json();
    const actual = verified.items.filter(item => serviceIds.has(item.variant_id));
    if (actual.length !== wanted.size || actual.some(item => !wanted.has(item.variant_id) || item.quantity !== 1)) {
      throw new Error('Your selected charges could not be confirmed.');
    }
    for (const [key, value] of Object.entries(selectedAttributes)) {
      if (String(verified.attributes?.[key] || '') !== String(value || '')) throw new Error('Your appointment details could not be confirmed.');
    }
    return verified;
  };
  return {root, update, defaultNotice, validate, syncCart, setNotice, inCartDrawer};
  };

  const refreshEstimate = (root, cart) => {
    let summary = root.querySelector('[data-dd-charge-summary]');
    if (!summary) {
      summary = document.createElement('p');
      summary.dataset.ddChargeSummary = '';
      summary.setAttribute('role', 'status');
      root.append(summary);
    }
    const services = cart.items.filter(item => serviceIds.has(item.variant_id));
    summary.textContent = services.length
      ? 'Included in your total: ' + services.map(item => item.product_title + ' — ' + money(item.final_line_price, cart.currency)).join('; ')
      : 'No delivery or optional service charges selected.';
  };
  const refreshing = new WeakSet();
  const refreshFromCart = root => {
    if (refreshing.has(root)) return;
    refreshing.add(root);
    fetch(shopRoot() + 'cart.js', {cache:'no-store', headers:{Accept:'application/json'}})
      .then(response => { if (!response.ok) throw new Error(); return response.json(); })
      .then(cart => { if (root.isConnected) refreshEstimate(root, cart); })
      .catch(() => {})
      .finally(() => refreshing.delete(root));
  };
  const save = controller => {
    queue = queue.catch(() => {}).then(async () => {
      if (!controller.root.isConnected) throw new Error('Your cart changed. Please review your options and try again.');
      const cart = await controller.syncCart();
      refreshEstimate(controller.root, cart);
      return cart;
    });
    return queue;
  };
  const setBusy = (root, busy) => {
    if (busy) root.setAttribute('aria-busy', 'true'); else root.removeAttribute('aria-busy');
    root.querySelectorAll('input,select,textarea').forEach(el => el.disabled = busy);
  };

  const changed = event => {
    const root = event.target.closest?.('[data-dd-scheduler]');
    if (!root || checkoutBusy) return;
    const controller = controllerFor(root);
    controller.update();
    controller.setNotice('Selections changed. Your total will update when you click “Apply options & update total” or “Check out.”');
  };
  document.addEventListener('input', changed);
  document.addEventListener('change', changed);

  const beginCheckout = (event, button, root) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutBusy) return;
    const controller = controllerFor(root);
    if (controller.inCartDrawer) {
      window.location.assign(shopRoot() + 'cart');
      return;
    }
    if (!controller.validate()) {
      controller.setNotice('Please complete the highlighted pickup or delivery details before checkout.');
      return;
    }
    checkoutBusy = true;
    if (button) button.disabled = true;
    controller.setNotice('Saving your options and updating your total…');
    setBusy(root, true);
    // Always save the current selections to the cart right before checkout,
    // so checkout reflects exactly what is selected on the page.
    save(controller).then(() => {
      try { sessionStorage.removeItem(appliedFlag); } catch {}
      const form = document.createElement('form');
      form.method = 'post';
      form.action = shopRoot() + 'cart';
      const field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'checkout';
      field.value = 'Check out';
      form.append(field);
      document.body.append(form);
      HTMLFormElement.prototype.submit.call(form);
    }).catch(error => {
      checkoutBusy = false;
      if (button) button.disabled = false;
      setBusy(root, false);
      controller.setNotice(error.message + ' Please try again.');
    });
  };

  const applyOptions = async (event, button) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutBusy) return;
    const root = button.closest('[data-dd-scheduler]');
    const controller = controllerFor(root);
    if (!controller.validate()) return;
    checkoutBusy = true;
    button.disabled = true;
    button.textContent = 'Updating total…';
    setBusy(root, true);
    try {
      await save(controller);
      try { sessionStorage.setItem(appliedFlag, '1'); } catch {}
      controller.setNotice('Options saved. Refreshing your cart total…');
      window.location.reload();
    } catch (error) {
      checkoutBusy = false;
      button.disabled = false;
      button.textContent = 'Apply options & update total';
      setBusy(root, false);
      controller.setNotice(error.message + ' Please try again.');
    }
  };

  document.addEventListener('click', event => {
    const apply = event.target.closest?.('[data-dd-apply]');
    if (apply) { applyOptions(event, apply); return; }
    const button = event.target.closest?.('#checkout, [name="checkout"], a[href="/checkout"]');
    if (!button) return;
    const root = button.closest('.cart-drawer__summary')?.querySelector('[data-dd-scheduler]')
      || button.parentElement?.parentElement?.querySelector('[data-dd-scheduler]')
      || document.querySelector('main [data-dd-scheduler]');
    if (root) beginCheckout(event, button, root);
  }, true);
  document.addEventListener('submit', event => {
    if (!event.target.matches('.cart-form') || !event.submitter?.matches('[name="checkout"],#checkout')) return;
    const root = document.querySelector('main [data-dd-scheduler]');
    if (root) beginCheckout(event, event.submitter, root);
  }, true);

  const initialize = () => document.querySelectorAll('[data-dd-scheduler]').forEach(root => {
    const isNew = !controllers.has(root);
    const controller = controllerFor(root);
    // The checkout button is rendered disabled until this script runs, and the
    // cart re-render after a quantity change puts that attribute back. Re-enable it.
    const button = checkoutFor(root);
    if (button && button.disabled && !checkoutBusy) button.disabled = false;
    if (checkoutBusy) return;
    if (isNew) {
      controller.defaultNotice();
      let applied = false;
      try { applied = sessionStorage.getItem(appliedFlag) === '1'; sessionStorage.removeItem(appliedFlag); } catch {}
      if (applied && !controller.inCartDrawer) controller.setNotice('Total updated. Your options are saved and checkout is ready.');
      refreshFromCart(root);
      return;
    }
    // Existing scheduler that was re-rendered: restore the notice and charge summary.
    const notice = root.querySelector('[data-dd-notice]');
    if (notice && !notice.textContent.trim()) controller.defaultNotice();
    if (!root.querySelector('[data-dd-charge-summary]')) refreshFromCart(root);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, {once:true});
  else initialize();
  new MutationObserver(initialize).observe(document.documentElement, {childList:true, subtree:true, attributes:true, attributeFilter:['disabled']});
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    checkoutBusy = false;
    document.querySelectorAll('[data-dd-scheduler]').forEach(root => {
      setBusy(root, false);
      const button = checkoutFor(root);
      if (button) button.disabled = false;
    });
  });
})();
